// src/utils/distance.js

/** Convert degrees to radians */
export function deg2rad(deg) {
  return (deg * Math.PI) / 180;
}

/** haversine formula (lat/lon in degrees) */
export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = deg2rad(Number(lat2) - Number(lat1));
  const dLon = deg2rad(Number(lon2) - Number(lon1));
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(Number(lat1))) *
      Math.cos(deg2rad(Number(lat2))) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Legacy name preserved */
export function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  return haversineKm(lat1, lon1, lat2, lon2);
}

/**
 * Flexible distance: supports:
 * - distanceInKm({lat, lon}, {lat, lon})
 * - distanceInKm([lat, lon], [lat, lon])
 * - distanceInKm(lat1, lon1, lat2, lon2)
 */
function _extractCoords(a, b, c, d) {
  // Helper to parse a single coordinate object or array
  const parseOne = (x) => {
    if (!x) return null;
    if (Array.isArray(x) && x.length >= 2) return [Number(x[0]), Number(x[1])];
    if (typeof x === 'object') {
      if (x.latitude != null && x.longitude != null) return [Number(x.latitude), Number(x.longitude)];
      if (x.lat != null && (x.lng != null || x.lon != null)) return [Number(x.lat), Number(x.lng ?? x.lon)];
    }
    return null; // Fixed: Now returns null on fail instead of grabbing outer variables
  };

  // If only 2 arguments are provided, try parsing them as objects/arrays
  if (c === undefined && d === undefined) {
    const r1 = parseOne(a);
    const r2 = parseOne(b);
    if (r1 && r2) return [r1[0], r1[1], r2[0], r2[1]];
  }

  // fallback: try to coerce provided values as 4 distinct arguments (best-effort)
  return [Number(a) || 0, Number(b) || 0, Number(c) || 0, Number(d) || 0];
}

export function distanceInKm(a, b, c, d) {
  const [lat1, lon1, lat2, lon2] = _extractCoords(a, b, c, d);
  return haversineKm(lat1, lon1, lat2, lon2);
}

/** alias */
export function getDistanceKm(a, b, c, d) {
  return distanceInKm(a, b, c, d);
}

/** default export object for code that imports DistanceUtil */
const DistanceUtil = {
  deg2rad,
  haversineKm,
  getDistanceFromLatLonInKm,
  distanceInKm,
  getDistanceKm,
};

export default DistanceUtil;